/**
 * Reporta errores del panel admin a Slack vía POST /api/admin/report-error.
 * Dedupe en cliente (15 min) para no spamear el canal.
 */

import type { AdminApiFetchInit } from "@/lib/adminApiFetch";

export type AdminErrorReport = {
  page: string;
  message: string;
  apiPath?: string;
  action?: string;
  status?: number | string;
  fingerprint?: string;
};

const sent = new Map<string, number>();
const CLIENT_DEDUP_MS = 15 * 60 * 1000;

type AdminFetchFn = (
  input: RequestInfo | URL,
  init?: AdminApiFetchInit,
) => Promise<Response>;

let adminFetchProvider: AdminFetchFn | null = null;

/** Registrado desde AdminLayout con useAdminAuth(). */
export function registerAdminErrorReporter(adminFetch: AdminFetchFn): void {
  adminFetchProvider = adminFetch;
}

function dedupeKey(r: AdminErrorReport): string {
  return [r.page, r.apiPath ?? "", r.action ?? "", r.fingerprint ?? r.message.slice(0, 80)].join("|");
}

export function reportAdminError(report: AdminErrorReport): void {
  if (typeof window === "undefined") return;

  const key = dedupeKey(report);
  const now = Date.now();
  const last = sent.get(key);
  if (last && now - last < CLIENT_DEDUP_MS) return;
  sent.set(key, now);

  const fetchFn = adminFetchProvider;
  if (!fetchFn) return;

  void fetchFn("/api/admin/report-error", {
    method: "POST",
    adminPriority: "low",
    body: JSON.stringify({
      page: report.page,
      message: report.message,
      apiPath: report.apiPath,
      action: report.action,
      status: report.status,
      fingerprint: report.fingerprint,
    }),
  }).catch(() => {
    /* best-effort */
  });
}

/** Normaliza catch de páginas admin y dispara Slack. */
export function captureAdminPageError(
  page: string,
  err: unknown,
  meta?: Omit<AdminErrorReport, "page" | "message">,
): string {
  const message = err instanceof Error ? err.message : String(err);
  reportAdminError({ page, message, ...meta });
  return message;
}